/**
 * @fileoverview Admin InfoTrack Searches — unified grouped view
 *
 * Purpose: Line-by-line audit trail of every InfoTrack search, grouped under
 * firm headers, filterable by firm (dropdown) and a free date range. Group
 * headers carry per-firm counts (total/completed/failed) and top products for
 * commission tracking; rows show each individual search.
 * URL: /admin/infotrack
 * Protection: Requires super_admin role.
 *
 * Dependencies:
 * - Convex: admin.infotrackSearches.listFirmsWithSearches, .listGroupedSearches
 */

import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";
import { dateInputToFromMs, dateInputToToMs } from "@/utils/format/dateRange";

export const Route = createFileRoute("/_app/_auth/admin/_layout/infotrack")({
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData(
      convexQuery(api.users.queries.getCurrentUser, {}),
    );
    if (!user?.isOwner) {
      throw redirect({ to: "/admin/master-library" });
    }
  },
  component: InfotrackSearchesPage,
});

const ALL_FIRMS = "all";

const STATUS_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  completed: "default",
  in_progress: "secondary",
  submitted: "secondary",
  approved: "secondary",
  draft: "outline",
  failed: "destructive",
  cancelled: "destructive",
};

function InfotrackSearchesPage() {
  const [firm, setFirm] = useState<string>(ALL_FIRMS);
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const { data: firmOptions } = useQuery(
    convexQuery(api.admin.infotrackSearches.listFirmsWithSearches, {}),
  );

  const queryArgs = useMemo(
    () => ({
      orgId: firm === ALL_FIRMS ? undefined : (firm as Id<"organizations">),
      from: dateInputToFromMs(fromDate),
      to: dateInputToToMs(toDate),
    }),
    [firm, fromDate, toDate],
  );

  const { data: groups } = useQuery(
    convexQuery(api.admin.infotrackSearches.listGroupedSearches, queryArgs),
  );

  const toggle = (orgId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });

  const clearFilters = () => {
    setFirm(ALL_FIRMS);
    setFromDate("");
    setToDate("");
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">InfoTrack Searches</h1>
        <p className="text-sm text-muted-foreground">
          Line-by-line audit trail of InfoTrack searches, grouped by firm.
          Filter by firm and date range for commission tracking.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Firm</label>
          <Select value={firm} onValueChange={setFirm}>
            <SelectTrigger className="w-[260px]">
              <SelectValue placeholder="All firms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_FIRMS}>All firms</SelectItem>
              {(firmOptions ?? []).map((f) => (
                <SelectItem key={f.orgId} value={f.orgId}>
                  {f.firmName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">From</label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-[170px]"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">To</label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-[170px]"
          />
        </div>

        <Button variant="outline" size="sm" onClick={clearFilters}>
          Clear
        </Button>
      </div>

      {groups && groups.length > 0 ? (
        <div className="flex flex-col gap-4">
          {groups.map((group) => {
            const isCollapsed = collapsed.has(group.orgId);
            return (
              <div key={group.orgId} className="rounded-lg border">
                <button
                  type="button"
                  onClick={() => toggle(group.orgId)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left"
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="font-semibold">{group.firmName}</span>
                  <span className="text-sm text-muted-foreground">
                    {group.totalSearches} searches · {group.completedCount}{" "}
                    completed · {group.failedCount} failed
                  </span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {group.perProduct
                      .slice(0, 3)
                      .map((p) => `${p.displayName} (${p.count})`)
                      .join(", ")}
                  </span>
                </button>

                {isCollapsed ? null : (
                  <div className="border-t">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Product</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Requested by</TableHead>
                          <TableHead>Completed at</TableHead>
                          <TableHead>Job</TableHead>
                          <TableHead>InfoTrack order</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.searches.map((s) => (
                          <TableRow key={s._id}>
                            <TableCell className="text-muted-foreground">
                              {new Date(s.createdAt).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {s.displayName}
                                </span>
                                {s.deleted ? (
                                  <span className="text-xs text-muted-foreground">
                                    deleted from history
                                  </span>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={STATUS_VARIANT[s.status] ?? "outline"}
                              >
                                {s.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{s.source}</Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {s.requesterName}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {s.completedAt
                                ? new Date(s.completedAt).toLocaleString()
                                : "—"}
                            </TableCell>
                            <TableCell>
                              <Link
                                to="/dashboard/jobs/$jobId"
                                params={{ jobId: s.jobId }}
                                className="text-primary underline-offset-2 hover:underline"
                              >
                                View job
                              </Link>
                            </TableCell>
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {s.infoTrackOrderId ?? "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border py-8 text-center text-muted-foreground">
          {groups === undefined ? "Loading..." : "No InfoTrack searches found."}
        </div>
      )}
    </div>
  );
}
